import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ConfirmText,
  Description,
  IconsWrapper,
  MoneyBackDescription,
  MoneyBackTitle,
  MoneyBackWrapper,
  OldPrice,
  PerDay,
  PlanCheck,
  PlanLi,
  PlanList,
  PlanTitle,
  Price,
  PriceWrapper,
  ReviewBlock,
  SmallItemsWrapper,
  Stars,
  Store,
  StoreButtons,
  StoreRating,
  StoreRatingsCount,
  StyledFeatureItem,
  StyledFullPrice,
  StyledPeriod,
  StyledPriceWrapper,
  TimerBlock,
  TimerBlockDiscount,
  Title,
  Wrapper,
} from './HeadwayPlans';
import {
  BestLabel,
  Label,
  Rate,
  ReviewBody,
  ReviewHead,
  ReviewItem,
  ReviewText,
  ReviewerName,
  ReviewerPhoto,
  StyledList,
  UserIcon,
} from './styled';
import { selectLandingType, selectPlan, selectPlanCoupon, selectShowCheckout, setDefaultPlanId } from './plansSlice';
import { PLANS } from '../../analytics/eCommerce';
import UIButton from '../../UI/UIButton';
import HeaderLogo from '../../UI/HeaderLogo';
import { TimerSimple } from '../../UI/Timer';

const IntroWrapper = styled(Wrapper)`
  padding: 0 0 24px;
`;

const StyledImage = styled.div`
  aspect-ratio: 327/255;
  width: 100%;
  background: url('../assets/plans/intro-paywall.png') center no-repeat;
  background-size: contain;
  margin: 32px 0;
`;

const FeaturesWrapper = styled.div`
  margin: 12px 0 32px;
  display: flex;
  justify-content: center;
`;

const StyledTitle = styled(Title)`
  line-height: 36.4px;
`;

const StyledBestLabel = styled(BestLabel)`
  border-radius: 7px;
  right: 50%;
  transform: translate(50%, -50%);
  height: 24px;
  line-height: 14.3px;
  font-weight: 700;
`;

const IntroOfferPlans = ({ onContinueClick, handleSetPlan }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [planDetails, setPlanDetails] = useState({});
  const plan = useSelector(selectPlan);
  const couponDetails = useSelector(selectPlanCoupon);
  const showCheckout = useSelector(selectShowCheckout);
  const landingType = useSelector(selectLandingType);

  const EXTENDED_CARD_BRANDS = useMemo(
    () => [
      { name: 'visa' },
      { name: 'mastercard' },
      { name: 'paypal' },
      { name: 'amex' },
      { name: 'discover' },
      { name: 'apple' },
      { name: 'google' },
    ],
    []
  );

  useEffect(() => {
    if (plan) {
      setPlanDetails(PLANS[plan]);
    }
  }, [plan]);

  useEffect(() => {
    if (!showCheckout) {
      handleSetPlan(currentPlans[0]?.id, currentPlans[0]?.coupon);
      dispatch(setDefaultPlanId(currentPlans[0]?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showCheckout]);

  const introOfferPaywall = useMemo(
    () => [
      {
        id: 'fm-monthly-intro7d-6.99-39.99',
        title: `1 ${t('week')}`,
        price: '0',
        cents: '99',
        oldPrice: '$1.42',
        fullPrice: '$6.99',
        oldFullPrice: '$15.99',
        week: `${t('per_day')}`,
        bestValue: true,
      },
      {
        id: 'fm-monthly-39.99',
        title: `1 ${t('month')}`,
        price: '0',
        cents: '64',
        oldPrice: '$0.96',
        fullPrice: '$19.99',
        oldFullPrice: '$39.99',
        week: t('per_day'),
        coupon: 'discount_20_usd_off_onetime',
      },
      {
        id: 'fm-3month-59.99',
        title: `3 ${t('months')}`,
        price: '0',
        cents: '44',
        oldPrice: '$0.50',
        fullPrice: '$39.99',
        oldFullPrice: '$59.99',
        week: t('per_day'),
        coupon: 'discount_20_usd_off_onetime',
      },
    ],
    [t]
  );

  const OFFERS = useMemo(
    () => ({
      intro_offer: introOfferPaywall,
    }),
    [introOfferPaywall]
  );

  const currentPlans = useMemo(
    () => OFFERS[landingType],
    [landingType, OFFERS]
  );

  /*  Lists  */
  const REVIEWS = useMemo(
    () => [
      {
        market: 'IOS',
        reviewer: 'Kevin Peters',
        reviewerSource: '../assets/reviewer1.png',
        text: t('review_1'),
      },
      {
        market: 'Play',
        reviewer: 'John Lee',
        reviewerSource: '../assets/reviewer2.png',
        text: t('review_2'),
      },
      {
        market: 'Play',
        reviewer: 'Megan Smith',
        reviewerSource: '../assets/reviewer3.png',
        text: t('review_3'),
      },
    ],
    [t]
  );

  const FEATURES = useMemo(
    () => [
      t('real_time_location'),
      t('location-history'),
      t('place_alerts'),
      t('sos_button'),
    ],
    [t]
  );

  const ICONS = EXTENDED_CARD_BRANDS;

  const STORES = [
    {
      name: 'App Store',
      event: 'appleStoreTapped',
      icon: '../assets/plans/app-store.svg',
      grade: 4.6,
      rating: '36.9K',
    },
    {
      name: 'Google Play',
      event: 'googlePlayTapped',
      icon: '../assets/plans/google-play.svg',
      grade: 4.5,
      rating: '412K',
    },
  ];
  /*  End Lists  */

  /*  Renders  */
  const renderPlans = (planInfo, index) => {
    const {
      id,
      title,
      price,
      cents,
      oldPrice,
      coupon,
      fullPrice,
      oldFullPrice,
      bestValue,
    } = planInfo;
    const isActive = plan === id;
    return (
      <PlanLi
        active={isActive}
        onClick={() => handleSetPlan(id, coupon)}
        id={'plan-button-' + (index + 1)}
        key={id}
      >
        {bestValue && <StyledBestLabel>{t('most_popular')}</StyledBestLabel>}
        <StyledPeriod>
          <PlanCheck active={isActive} />
          <PlanTitle>
            <div>{title}</div>
            <StyledFullPrice>
              <strike>{oldFullPrice}</strike> {fullPrice}
            </StyledFullPrice>
          </PlanTitle>
        </StyledPeriod>
        <StyledPriceWrapper isTrial>
          <OldPrice>{oldPrice}</OldPrice>
          <PriceWrapper active={isActive}>
            <Price>{price}</Price>
            <SmallItemsWrapper>
              <span>{cents}</span>
              <PerDay>{t('per_day')}</PerDay>
            </SmallItemsWrapper>
          </PriceWrapper>
        </StyledPriceWrapper>
      </PlanLi>
    );
  };

  const renderReview = (r, i) => (
    <ReviewItem key={i}>
      <ReviewHead>
        <UserIcon imgUserPath={'../assets/app-icon.svg'} />
        <Label>{r.market}</Label>
        <Rate imgStarPath={'../assets/stars.png'} />
      </ReviewHead>
      <ReviewBody>
        <ReviewerPhoto src={r.reviewerSource} alt={r.reviewer} />
        <ReviewerName>{r.reviewer}</ReviewerName>
      </ReviewBody>
      <ReviewText>{r.text}</ReviewText>
    </ReviewItem>
  );

  const renderFeature = (i, index) => (
    <StyledFeatureItem key={index}>
      {i}
    </StyledFeatureItem>
  );

  const renderIcon = (item) => (
    <img
      key={item.name}
      src={`../assets/checkout/trial/${item.name}.png`}
      alt={item.name}
    />
  );

  const renderStore = (i, index) => (
    <Store key={index} img={i.icon}>
      <StoreRating>{i.grade}</StoreRating>
      <Stars />
      <StoreRatingsCount>
        {i.rating} {t('ratings')}
      </StoreRatingsCount>
    </Store>
  );
  /*  End Renders  */

  const onLinkClick = ({ target: { id } }) => {
		const termsLink = 'https://familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf';
		const links = {
			privacy: 'https://terms.familo.net/privacy-info',
			terms: termsLink,
			refund: termsLink,
			'terms-of-use': termsLink,
      'fo-link': 'https://familo.net'
		};

    if (id && links[id]) window.open(links[id]);
  };

  /*  Components  */
  const disclaimer = (
    <ConfirmText
      dangerouslySetInnerHTML={{ __html: t('disclaimer_intro') }}
      onClick={(e) => onLinkClick(e)}
    />
  );

  const TrialDisclaimer = () => {
    const { price, periodPrice, duration } = planDetails;
    const { couponPrice } = couponDetails;
    const finalPrice = +(periodPrice - couponPrice).toFixed(2);

    return (
      <ConfirmText
        dangerouslySetInnerHTML={{
          __html: t('trial_disclaimer', {
            price,
            periodPrice: finalPrice,
            duration,
          }),
        }}
        onClick={(e) => onLinkClick(e)}
      />
    );
  };

  const continueButton = (customStyles, id) => (
    <UIButton
      onClick={onContinueClick}
      bottomPosition={'16px'}
      displayStickyBtn={false}
      position={'fixed'}
      displayStubBtn={false}
      customBeforeHeight={false}
      customStyles={customStyles}
      customId={`paywall-continue-button-${id}`}
    />
  );

  /*  End Components  */

  return (
    <IntroWrapper>
      <HeaderLogo />
      <TimerBlock>
        <div id="wrapper">
          <TimerBlockDiscount>{t('discount_reserved')}</TimerBlockDiscount>
          <TimerSimple />
        </div>
        {continueButton('width:105px;min-height:40px;height:40px;margin:0; padding: 0;', 1)}
      </TimerBlock>
      <StyledTitle customMargin={'0 0 16px 0'}>
        {t('make_sure_title')}
      </StyledTitle>
      <Description>{t('get_full_history')}</Description>
      <StyledImage />
      <StyledTitle customMargin={'32px 0 0'}>
        {t('what_you_get_with_gz')}
      </StyledTitle>
      <FeaturesWrapper>
        <StyledList>{FEATURES.map(renderFeature)}</StyledList>
      </FeaturesWrapper>
      <StyledTitle>{t('choose_your_plan')}</StyledTitle>
      <PlanList>{currentPlans.map(renderPlans)}</PlanList>
      {continueButton('margin-top:8px; min-height: 56px', 2)}
      <IconsWrapper>{ICONS.map(renderIcon)}</IconsWrapper>
      <TrialDisclaimer />
      <StoreButtons>{STORES.map(renderStore)}</StoreButtons>
      <StyledTitle customMargin={'10px 0 0'}>{t('people_love')}</StyledTitle>
      <ReviewBlock>{REVIEWS.map(renderReview)}</ReviewBlock>
      <MoneyBackWrapper>
        <MoneyBackTitle>{t('money_back_guarantee')}</MoneyBackTitle>
        <MoneyBackDescription
          dangerouslySetInnerHTML={{ __html: t('guarantee_text') }}
          onClick={(e) => onLinkClick(e)}
        />
      </MoneyBackWrapper>
      <Title dangerouslySetInnerHTML={{ __html: t('safe_checkout') }} />
      <IconsWrapper customMargin={'24px 0'}>
        {ICONS.map(renderIcon)}
      </IconsWrapper>
      {continueButton('margin: 0px; min-height: 56px', 3)}
      {disclaimer}
    </IntroWrapper>
  );
};

export default IntroOfferPlans;
